/**
 * Boîte à outils — la fenêtre (docs « Outils pédagogiques des SKILLCARDS »).
 *
 * Ouverte depuis le player (icône sous Cours et Calculatrice, ou un outil de
 * la fenêtre Cours), depuis la feuille de brouillon ou depuis la page d'une
 * SKILLCARD. Elle liste d'abord les outils reliés à la carte par son auteur
 * (`card_tools`), à défaut ceux que la carte désigne ou de son domaine, puis
 * les autres outils du niveau ; toucher un outil l'ouvre dans la même
 * fenêtre. Plein écran sur téléphone, panneau à droite sur ordinateur (la
 * question reste visible).
 *
 * Ouverte depuis une feuille encore modifiable, elle reçoit `onSend` : un outil
 * peut alors poser une valeur dans la ligne en cours (même prix qu'un
 * résultat de calculatrice).
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { ArrowLeft, X } from 'lucide-react';
import { trackAppEvent } from '@/lib/analytics/track';
import {
  toolById,
  toolsForContext,
  type ToolDef,
  type ToolId,
  type ToolboxContext,
} from './catalog';
import type { ToolChip } from './tool-types';
import { ToolList } from './tool-list';
import { TrigCircleTool } from './tools/trig-circle/trig-circle-tool';
import { TimesTableTool } from './tools/times-table/times-table-tool';
import { FunctionPlotterTool } from './tools/function-plotter/function-plotter-tool';
import { PythagorasTool } from './tools/pythagoras/pythagoras-tool';
import { ThalesTool } from './tools/thales/thales-tool';
import { RightTriangleTool } from './tools/right-triangle/right-triangle-tool';
import { LineSlidersTool } from './tools/line-sliders/line-sliders-tool';
import { VectorSumTool } from './tools/vector-sum/vector-sum-tool';
import { DotProductTool } from './tools/dot-product/dot-product-tool';
import { PascalTool } from './tools/pascal/pascal-tool';
import { ModularClockTool } from './tools/modular-clock/modular-clock-tool';
import { TaylorTool } from './tools/taylor/taylor-tool';
import { SequencesTool } from './tools/sequences/sequences-tool';
import { PartialSumsTool } from './tools/partial-sums/partial-sums-tool';
import { RandomLauncherTool } from './tools/random-launcher/random-launcher-tool';
import { BayesTreeTool } from './tools/bayes-tree/bayes-tree-tool';
import { GaltonBoardTool } from './tools/galton-board/galton-board-tool';
import { StatsSeriesTool } from './tools/stats-series/stats-series-tool';
import { PercentEvolutionsTool } from './tools/percent-evolutions/percent-evolutions-tool';
import { PrimesGridTool } from './tools/primes-grid/primes-grid-tool';
import { PlaceValueTool } from './tools/place-value/place-value-tool';
import { DurationClockTool } from './tools/duration-clock/duration-clock-tool';
import { NumberMachineTool } from './tools/number-machine/number-machine-tool';
import { BattleshipTool } from './tools/coordinates-battleship/battleship-tool';
import { RootSquareTool } from './tools/root-square/root-square-tool';
import { IntervalBuilderTool } from './tools/interval-builder/interval-builder-tool';
import { NumberSetsTool } from './tools/number-sets/number-sets-tool';
import { MovingPointsTool } from './tools/moving-points/moving-points-tool';
import { ReferenceFunctionsTool } from './tools/reference-functions/reference-functions-tool';
import { CompoundETool } from './tools/compound-e/compound-e-tool';
import { PiPolygonTool } from './tools/pi-polygon/pi-polygon-tool';
import { CircleEquationTool } from './tools/circle-equation/circle-equation-tool';
import { ChordTangentTool } from './tools/chord-tangent/chord-tangent-tool';
import { RiemannSumsTool } from './tools/riemann-sums/riemann-sums-tool';
import { UrnDrawsTool } from './tools/urn-draws/urn-draws-tool';
import { SmartCalcTool } from './tools/smart-calc/smart-calc-tool';
import { BalanceTool } from './tools/balance/balance-tool';
import { AlgebraTilesTool } from './tools/algebra-tiles/algebra-tiles-tool';
import { FractionWallTool } from './tools/fraction-wall/fraction-wall-tool';
import { SignedCountersTool } from './tools/signed-counters/signed-counters-tool';
import { ProportionTableTool } from './tools/proportion-table/proportion-table-tool';
import { PowersTool } from './tools/powers/powers-tool';
import { TransformationsTool } from './tools/transformations/transformations-tool';
import { AnglesTool } from './tools/angles/angles-tool';
import { ComplexPlaneTool } from './tools/complex-plane/complex-plane-tool';
import { MarkovGraphTool } from './tools/markov-graph/markov-graph-tool';
import { MatrixCalcTool } from './tools/matrix-calc/matrix-calc-tool';
import { LogicTool } from './tools/logic/logic-tool';
import { PythonSandboxTool } from './tools/python-sandbox/python-sandbox-tool';
import { UnitConverterTool } from './tools/unit-converter/unit-converter-tool';
import { GeoboardTool } from './tools/geoboard/geoboard-tool';
import { GridRobotTool } from './tools/grid-robot/robot-tool';
import { SolidNetsTool } from './tools/solid-nets/solid-nets-tool';
import { SolidSectionsTool } from './tools/solid-sections/solid-sections-tool';
import { NumberLineTool } from './tools/number-line/number-line-tool';
import { MeanLevelingTool } from './tools/mean-leveling/mean-leveling-tool';
import { AreaMultiplicationTool } from './tools/area-multiplication/area-multiplication-tool';
import { RulerCompassTool } from './tools/ruler-compass/ruler-compass-tool';
import { GraphicSolveTool } from './tools/graphic-solve/graphic-solve-tool';
import { SamplesTool } from './tools/samples/samples-tool';
import { ParabolaTool } from './tools/parabola/parabola-tool';
import { TangentTool } from './tools/tangent/tangent-tool';
import { LotteryWheelTool } from './tools/lottery-wheel/lottery-wheel-tool';
import { ConcentrationTool } from './tools/concentration/concentration-tool';
import { BisectionTool } from './tools/bisection/bisection-tool';
import { DirectionFieldTool } from './tools/direction-field/direction-field-tool';
import { SlideRuleTool } from './tools/slide-rule/slide-rule-tool';
import { LinearMapTool } from './tools/linear-map/linear-map-tool';
import { LawSimTool } from './tools/law-sim/law-sim-tool';
import { GramSchmidtTool } from './tools/gram-schmidt/gram-schmidt-tool';
import { ReactionProgressTool } from './tools/reaction-progress/reaction-progress-tool';
import { LagrangeTool } from './tools/lagrange/lagrange-tool';
import { LevelGradientTool } from './tools/level-gradient/level-gradient-tool';
import { ParamCurveTool } from './tools/param-curve/param-curve-tool';
import { SpaceFrameTool } from './tools/space-frame/space-frame-tool';
import { RedoxTool } from './tools/redox/redox-tool';
import { CircuitTool } from './tools/circuit/circuit-tool';
import { MotionTool } from './tools/motion/motion-tool';
import { WavesTool } from './tools/waves/waves-tool';
import { OpticsTool } from './tools/optics/optics-tool';
import { AtomTool } from './tools/atom/atom-tool';
import { PhTool } from './tools/ph/ph-tool';
import { MatterTool } from './tools/matter/matter-tool';
import { GasTool } from './tools/gas/gas-tool';
import { ColorsTool } from './tools/colors/colors-tool';
import { RcTool } from './tools/rc/rc-tool';

export interface ToolboxSheetProps {
  context: ToolboxContext;
  onClose: () => void;
  /** présent seulement si la boîte vient d'une feuille encore modifiable */
  onSend?: (chip: ToolChip) => void;
  /** HORA retirés par valeur posée dans la feuille */
  sendCost?: number;
  /** ouvrir directement cet outil */
  initialTool?: ToolId | null;
  /** titre des outils mis en avant (« Pour cette SKILLCARD » depuis une carte) */
  featuredTitle?: string;
}

export function ToolboxSheet({
  context,
  onClose,
  onSend,
  sendCost = 0,
  initialTool = null,
  featuredTitle = 'Pour cette question',
}: ToolboxSheetProps) {
  const [openId, setOpenId] = useState<ToolId | null>(initialTool);
  const selection = useMemo(() => toolsForContext(context), [context]);
  const tool = openId ? toolById(openId) : null;
  const dialogRef = useRef<HTMLElement>(null);
  const bodyRef = useRef<HTMLDivElement>(null);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  const openIdRef = useRef(openId);
  openIdRef.current = openId;

  // page derrière bloquée, Échap (avant la feuille de brouillon), focus rendu à la sortie
  useEffect(() => {
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const back = document.activeElement as HTMLElement | null;
    dialogRef.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      // un outil qui a une étape en cours (construction…) garde Échap pour l'annuler
      if ((e.target as Element | null)?.closest?.('[data-local-escape]')) return;
      e.stopPropagation();
      e.preventDefault();
      if (openIdRef.current) setOpenId(null);
      else onCloseRef.current();
    };
    window.addEventListener('keydown', onKey, true);
    return () => {
      document.body.style.overflow = previous;
      window.removeEventListener('keydown', onKey, true);
      back?.focus?.();
    };
  }, []);

  useEffect(() => {
    trackAppEvent('page_view', openId ? `/outils/${openId}` : '/outils');
  }, [openId]);

  const open = (id: ToolId) => {
    setOpenId(id);
    bodyRef.current?.scrollTo?.({ top: 0 });
  };

  const empty = selection.featured.length === 0 && selection.others.length === 0;

  return createPortal(
    <div className="fixed inset-0 z-[80] flex items-end justify-center md:items-stretch md:justify-end">
      <div
        className="absolute inset-0 bg-black/40 backdrop-blur-[2px] md:bg-black/10 md:backdrop-blur-none"
        onClick={onClose}
        aria-hidden
      />
      <section
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="toolbox-title"
        tabIndex={-1}
        data-testid="toolbox-sheet"
        className="ocean:bg-[#061433] native:h-[calc(100dvh-env(safe-area-inset-top)-0.5rem)] relative flex h-[calc(100dvh-0.5rem)] w-full flex-col overflow-hidden rounded-t-3xl bg-white shadow-2xl outline-none md:h-full md:w-[28rem] md:rounded-none md:rounded-l-3xl"
      >
        <header className="border-ink-100 flex flex-none items-center gap-1 border-b px-2 py-2">
          {tool ? (
            <button
              type="button"
              onClick={() => setOpenId(null)}
              aria-label="Tous les outils"
              className="text-ink-600 hover:bg-ink-100 flex min-h-11 min-w-11 items-center justify-center rounded-xl active:scale-[0.98]"
            >
              <ArrowLeft className="h-5 w-5" aria-hidden />
            </button>
          ) : (
            <span className="w-2" aria-hidden />
          )}
          <h2
            id="toolbox-title"
            className="font-display text-ink-900 min-w-0 flex-1 truncate text-[17px] font-bold"
          >
            {tool ? tool.title : 'Boîte à outils'}
          </h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="Fermer la boîte à outils"
            className="text-ink-400 hover:bg-ink-100 hover:text-ink-700 flex min-h-11 min-w-11 items-center justify-center rounded-xl active:scale-[0.98]"
          >
            <X className="h-5 w-5" aria-hidden />
          </button>
        </header>

        <div
          ref={bodyRef}
          className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-4 pb-[max(1.25rem,env(safe-area-inset-bottom))] pt-4"
        >
          {tool ? (
            <ToolView tool={tool} level={context.level} onSend={onSend} sendCost={sendCost} />
          ) : (
            <div className="space-y-5">
              {onSend && sendCost > 0 && (
                <p className="bg-accent-50 text-ink-800 rounded-2xl px-3 py-2 text-sm">
                  Calculatrice non autorisée sur cet exercice : chaque valeur que tu mets dans ta
                  feuille depuis un outil coûte {sendCost} HORA.
                </p>
              )}
              {selection.featured.length > 0 && (
                <ToolList title={featuredTitle} tools={selection.featured} onOpen={open} />
              )}
              {selection.others.length > 0 && (
                <ToolList
                  title={
                    selection.featured.length > 0
                      ? 'Autres outils de ton niveau'
                      : 'Outils de ton niveau'
                  }
                  tools={selection.others}
                  onOpen={open}
                />
              )}
              {empty && <p className="text-ink-500 text-sm">Pas encore d’outil pour ce niveau.</p>}
            </div>
          )}
        </div>
      </section>
    </div>,
    document.body,
  );
}

function ToolView({
  tool,
  level,
  onSend,
  sendCost,
}: {
  tool: ToolDef;
  level: string | null;
  onSend?: (chip: ToolChip) => void;
  sendCost: number;
}) {
  switch (tool.id) {
    case 'trig-circle':
      return <TrigCircleTool onSend={onSend} sendCost={sendCost} />;
    case 'times-table':
      return <TimesTableTool onSend={onSend} sendCost={sendCost} />;
    case 'function-plotter':
      return <FunctionPlotterTool onSend={onSend} sendCost={sendCost} level={level} />;
    case 'pythagoras-squares':
      return <PythagorasTool onSend={onSend} sendCost={sendCost} />;
    case 'thales':
      return <ThalesTool onSend={onSend} sendCost={sendCost} />;
    case 'right-triangle-trig':
      return <RightTriangleTool onSend={onSend} sendCost={sendCost} />;
    case 'line-sliders':
      return <LineSlidersTool onSend={onSend} sendCost={sendCost} />;
    case 'vector-sum':
      return <VectorSumTool onSend={onSend} sendCost={sendCost} />;
    case 'dot-product':
      return <DotProductTool onSend={onSend} sendCost={sendCost} />;
    case 'pascal-triangle':
      return <PascalTool onSend={onSend} sendCost={sendCost} />;
    case 'modular-clock':
      return <ModularClockTool onSend={onSend} sendCost={sendCost} />;
    case 'taylor':
      return <TaylorTool onSend={onSend} sendCost={sendCost} />;
    case 'sequences':
      return <SequencesTool onSend={onSend} sendCost={sendCost} level={level} />;
    case 'partial-sums':
      return <PartialSumsTool onSend={onSend} sendCost={sendCost} />;
    case 'random-launcher':
      return <RandomLauncherTool onSend={onSend} sendCost={sendCost} level={level} />;
    case 'bayes-tree':
      return <BayesTreeTool onSend={onSend} sendCost={sendCost} level={level} />;
    case 'galton-board':
      return <GaltonBoardTool onSend={onSend} sendCost={sendCost} level={level} />;
    case 'stats-series':
      return <StatsSeriesTool onSend={onSend} sendCost={sendCost} level={level} />;
    case 'percent-evolutions':
      return <PercentEvolutionsTool onSend={onSend} sendCost={sendCost} level={level} />;
    case 'primes-grid':
      return <PrimesGridTool onSend={onSend} sendCost={sendCost} level={level} />;
    case 'place-value':
      return <PlaceValueTool onSend={onSend} sendCost={sendCost} />;
    case 'duration-clock':
      return <DurationClockTool onSend={onSend} sendCost={sendCost} level={level} />;
    case 'number-machine':
      return <NumberMachineTool onSend={onSend} sendCost={sendCost} level={level} />;
    case 'coordinates-battleship':
      return <BattleshipTool onSend={onSend} sendCost={sendCost} level={level} />;
    case 'root-square':
      return <RootSquareTool onSend={onSend} sendCost={sendCost} level={level} />;
    case 'interval-builder':
      return <IntervalBuilderTool onSend={onSend} sendCost={sendCost} />;
    case 'number-sets':
      return <NumberSetsTool onSend={onSend} sendCost={sendCost} />;
    case 'moving-points':
      return <MovingPointsTool onSend={onSend} sendCost={sendCost} />;
    case 'reference-functions':
      return <ReferenceFunctionsTool onSend={onSend} sendCost={sendCost} />;
    case 'compound-e':
      return <CompoundETool onSend={onSend} sendCost={sendCost} level={level} />;
    case 'circle-equation':
      return <CircleEquationTool onSend={onSend} sendCost={sendCost} />;
    case 'chord-tangent':
      return <ChordTangentTool onSend={onSend} sendCost={sendCost} />;
    case 'riemann-sums':
      return <RiemannSumsTool onSend={onSend} sendCost={sendCost} level={level} />;
    case 'urn-draws':
      return <UrnDrawsTool onSend={onSend} sendCost={sendCost} level={level} />;
    case 'smart-calc':
      return <SmartCalcTool onSend={onSend} sendCost={sendCost} level={level} />;
    case 'balance':
      return <BalanceTool onSend={onSend} sendCost={sendCost} level={level} />;
    case 'algebra-tiles':
      return <AlgebraTilesTool onSend={onSend} sendCost={sendCost} level={level} />;
    case 'fraction-wall':
      return <FractionWallTool onSend={onSend} sendCost={sendCost} level={level} />;
    case 'signed-counters':
      return <SignedCountersTool onSend={onSend} sendCost={sendCost} level={level} />;
    case 'proportion-table':
      return <ProportionTableTool onSend={onSend} sendCost={sendCost} level={level} />;
    case 'powers':
      return <PowersTool onSend={onSend} sendCost={sendCost} level={level} />;
    case 'transformations':
      return <TransformationsTool sendCost={sendCost} level={level} />;
    case 'angles':
      return <AnglesTool sendCost={sendCost} level={level} />;
    case 'complex-plane':
      return <ComplexPlaneTool onSend={onSend} sendCost={sendCost} level={level} />;
    case 'markov-graph':
      return <MarkovGraphTool onSend={onSend} sendCost={sendCost} />;
    case 'matrix-calc':
      return <MatrixCalcTool onSend={onSend} sendCost={sendCost} />;
    case 'logic':
      return <LogicTool sendCost={sendCost} level={level} />;
    case 'python-sandbox':
      return <PythonSandboxTool sendCost={sendCost} level={level} />;
    case 'unit-converter':
      return <UnitConverterTool onSend={onSend} sendCost={sendCost} level={level} />;
    case 'geoboard':
      return <GeoboardTool onSend={onSend} sendCost={sendCost} level={level} />;
    case 'grid-robot':
      return <GridRobotTool sendCost={sendCost} />;
    case 'solid-nets':
      return <SolidNetsTool level={level} />;
    case 'solid-sections':
      return <SolidSectionsTool />;
    case 'number-line':
      return <NumberLineTool level={level} />;
    case 'mean-leveling':
      return <MeanLevelingTool level={level} />;
    case 'area-multiplication':
      return <AreaMultiplicationTool sendCost={sendCost} level={level} />;
    case 'ruler-compass':
      return <RulerCompassTool level={level} />;
    case 'graphic-solve':
      return <GraphicSolveTool level={level} />;
    case 'samples':
      return <SamplesTool />;
    case 'parabola':
      return <ParabolaTool />;
    case 'tangent':
      return <TangentTool level={level} />;
    case 'lottery-wheel':
      return <LotteryWheelTool />;
    case 'concentration':
      return <ConcentrationTool />;
    case 'bisection':
      return <BisectionTool level={level} />;
    case 'direction-field':
      return <DirectionFieldTool />;
    case 'slide-rule':
      return <SlideRuleTool />;
    case 'pi-polygon':
      return <PiPolygonTool sendCost={sendCost} level={level} />;
    case 'linear-map':
      return <LinearMapTool level={level} />;
    case 'law-sim':
      return <LawSimTool level={level} />;
    case 'gram-schmidt':
      return <GramSchmidtTool />;
    case 'reaction-progress':
      return <ReactionProgressTool level={level} />;
    case 'lagrange':
      return <LagrangeTool />;
    case 'level-gradient':
      return <LevelGradientTool />;
    case 'param-curve':
      return <ParamCurveTool />;
    case 'space-frame':
      return <SpaceFrameTool level={level} />;
    case 'redox':
      return <RedoxTool />;
    case 'circuit':
      return <CircuitTool level={level} />;
    case 'motion':
      return <MotionTool level={level} />;
    case 'waves':
      return <WavesTool level={level} />;
    case 'optics':
      return <OpticsTool level={level} />;
    case 'atom':
      return <AtomTool level={level} />;
    case 'ph':
      return <PhTool level={level} />;
    case 'matter':
      return <MatterTool level={level} />;
    case 'gas':
      return <GasTool level={level} />;
    case 'colors':
      return <ColorsTool level={level} />;
    case 'rc':
      return <RcTool level={level} />;
  }
}
